//Footer Menu Imports
import AbcIcon from '@mui/icons-material/Abc';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { TextStyleExtended } from './extensions/FontSize';

import { secondarySentence } from '../../../assets/styles/text.style';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import '../../../assets/styles/editor/styles.css';
import useDecryption from '../../../hooks/cryptography/decryptText';
import useEncryption from '../../../hooks/cryptography/encryptText';
import { Language } from '../../../interfaces/Language';
import { RootState } from '../../../utils/store';


export interface TranslatedDraft {
  detected_languages: Language[],
  message: string,
  target_language: Language,
}

interface TranslateDraftMessageVariables {
  loadingDraftTranslation: boolean;
  setLoadingDraftTranslation: React.Dispatch<React.SetStateAction<boolean>>;
  draftLanguage: string;
  setDraftLanguage: React.Dispatch<React.SetStateAction<string>>;
  switchDraftMessage: boolean;
  setSwitchDraftMessage: React.Dispatch<React.SetStateAction<boolean>>;
  originalDraftMessage: string;
  setOriginalDraftMessage: React.Dispatch<React.SetStateAction<string>>;
  translatedDraftMessage: string;
  setTranslatedDraftMessage: React.Dispatch<React.SetStateAction<string>>;
}

interface StandardTextEditorProps {
  text: string;
  expand?: boolean;
  customPlaceholder?: string;
  isEditable?: boolean;
  variant?: string;
  messageButton?: JSX.Element;
  attachedItems?: JSX.Element;
  enableTranslate?: boolean;
  onChangeText: (content: string) => void;
  setTranslatedDraftObject?: (originalMessage: string, translatedObject: TranslatedDraft | null) => void,
  translatedDraftMessageVariables?: TranslateDraftMessageVariables;
}

const StandardTextEditor = ({
  text,
  expand = false,
  customPlaceholder,
  isEditable = true,
  variant = 'default',
  messageButton,
  attachedItems,
  enableTranslate,
  onChangeText,
  setTranslatedDraftObject,
  translatedDraftMessageVariables,
}: StandardTextEditorProps) => {
  const [formattingEnabled, setFormattingEnabled] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorHeadingEl, setAnchorHeadingEl] = useState<null | HTMLElement>(
    null,
  );
  const openFontSizeMenu = Boolean(anchorEl);
  const openHeadingMenu = Boolean(anchorHeadingEl);
  const {
    loadingDraftTranslation,
    setLoadingDraftTranslation,
    draftLanguage,
    setDraftLanguage,
    switchDraftMessage,
    setSwitchDraftMessage,
    originalDraftMessage,
    setOriginalDraftMessage,
    translatedDraftMessage,
    setTranslatedDraftMessage,
  } = translatedDraftMessageVariables || {};
  const intl = useIntl();
  const { decryptText } = useDecryption();
  const { encryptText } = useEncryption();
  const tenantLanguages = useSelector(
    (state: RootState) => state.tenant.languages,
  );

  const enableFormattingShortcuts = Extension.create({
    addKeyboardShortcuts() {
      return {
        'Mod-b': () => {
          setFormattingEnabled(true);
          return false;
        },
        'Mod-i': () => {
          setFormattingEnabled(true);
          return false;
        },
        'Mod-u': () => {
          setFormattingEnabled(true);
          return false;
        },
      };
    },
  });

  const extensions = [
    StarterKit,
    Link,
    Underline,
    enableFormattingShortcuts,
    TextStyleExtended,
    Placeholder.configure({
      placeholder:
        customPlaceholder ??
        intl.formatMessage({
          defaultMessage: 'Kindly provide more details in your report.',
        }),
    }),
  ];

  const fontSizes = {
    small: '12',
    normal: '16',
    medium: '18',
    large: '24',
  };

  const headingSizes = {
    one: '28px',
    two: '21px',
    three: '16px',
    four: '14px',
    five: '12px',
    six: '9px',
  };

  const toolTipMessages = {
    formatting: intl.formatMessage({
      defaultMessage: 'Enable/Disable text formatting',
    }),
    bold: intl.formatMessage({ defaultMessage: 'Bold (Ctrl/Cmd + B)' }),
    underline: intl.formatMessage({
      defaultMessage: 'Underline (Ctrl/Cmd + U)',
    }),
    italic: intl.formatMessage({ defaultMessage: 'Italic (Ctrl/Cmd + I)' }),
    bulletList: intl.formatMessage({ defaultMessage: 'Unordered List' }),
    numberedList: intl.formatMessage({ defaultMessage: 'Ordered List' }),
    fontSize: intl.formatMessage({ defaultMessage: 'Font Size' }),
    heading: intl.formatMessage({ defaultMessage: 'Headings' }),
  };

  const initialEditor = useEditor({
    extensions: extensions,
    onUpdate: ({ editor }) => {
      onChangeText(editor.getHTML());
    },
    content: text,
    autofocus: false,
  });

  initialEditor?.setOptions({ editable: isEditable });

  //Syncs text content with expanded version of itself
  useEffect(() => {
    if (!initialEditor) {
      return;
    }
    const htmlContent = initialEditor.getHTML();
    if (htmlContent !== text) {
      initialEditor.commands.setContent(text);
    }
  }, [initialEditor, text]);

  // When a language is selected this useEffect is executed.
  // Translate the text to the selected language.
  useEffect(() => {
    if (translatedDraftMessageVariables) {
      if (draftLanguage && originalDraftMessage === '') {
        setLoadingDraftTranslation?.(true);
        setOriginalDraftMessage?.(text);
        encryptText(text).then(cipherText => {
          axiosInstance().post('/report-chat-messages/translate', {
            target_language_id: tenantLanguages.find(item => item.code === draftLanguage)?.id,
            message: cipherText,
          }).then(response => {
            if (cipherText !== '' && setTranslatedDraftObject)
              setTranslatedDraftObject(cipherText, response.data);

            decryptText(response.data.message).then(decryptedText => {
              setTranslatedDraftMessage?.(decryptedText);
              onChangeText(decryptedText);
              setLoadingDraftTranslation?.(false);
              setSwitchDraftMessage?.(false);
            });
          });
        });
      }
    }
  }, [decryptText, draftLanguage, encryptText, onChangeText, originalDraftMessage, setLoadingDraftTranslation, setOriginalDraftMessage, setSwitchDraftMessage, setTranslatedDraftMessage, setTranslatedDraftObject, tenantLanguages, text, translatedDraftMessageVariables]);

  useEffect(() => {
    if (!loadingDraftTranslation && translatedDraftMessage) {
      if (switchDraftMessage) {
        onChangeText(originalDraftMessage || '');
      } else {
        onChangeText(translatedDraftMessage);
      }
    }
  }, [loadingDraftTranslation,
    onChangeText,
    originalDraftMessage,
    switchDraftMessage,
    translatedDraftMessage]);

  if (!initialEditor) {
    return <></>;
  }

  //Editor Menu Functions

  const handleToggleBold = () => {
    initialEditor.chain().focus().toggleBold().run();
  };

  const handleToggleUnderline = () => {
    initialEditor.chain().focus().toggleUnderline().run();
  };

  const handleToggleItalic = () => {
    initialEditor.chain().focus().toggleItalic().run();
  };

  const handleToggleBulletList = () => {
    initialEditor.chain().focus().toggleBulletList().run();
  };

  const handleToggleNumberedList = () => {
    initialEditor.chain().focus().toggleOrderedList().run();
  };

  const handleToggleNormalFontSize = () => {
    initialEditor.chain().focus().unsetFontSize().run();
  };

  const handleToggleMediumFontSize = () => {
    initialEditor.chain().focus().setFontSize(fontSizes.medium).run();
  };

  const handleToggleLargeFontSize = () => {
    initialEditor.chain().focus().setFontSize(fontSizes.large).run();
  };

  const handleToggleSmallFontSize = () => {
    initialEditor.chain().focus().setFontSize(fontSizes.small).run();
  };

  // Due to Typescript issues with getting Level type from TipTap and level cant be used with number, each heading toggle must be a separate function
  const handleToggleHeadingOne = () => {
    initialEditor.chain().focus().toggleHeading({ level: 1 }).run();
  };

  const handleToggleHeadingTwo = () => {
    initialEditor.chain().focus().toggleHeading({ level: 2 }).run();
  };
  const handleToggleHeadingThree = () => {
    initialEditor.chain().focus().toggleHeading({ level: 3 }).run();
  };
  const handleToggleHeadingFour = () => {
    initialEditor.chain().focus().toggleHeading({ level: 4 }).run();
  };
  const handleToggleHeadingFive = () => {
    initialEditor.chain().focus().toggleHeading({ level: 5 }).run();
  };
  const handleToggleHeadingSix = () => {
    initialEditor.chain().focus().toggleHeading({ level: 6 }).run();
  };

  const handleHeadingButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorHeadingEl(event.currentTarget);
  };

  const handleFontSizeButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHeading = () => {
    setAnchorHeadingEl(null);
  };

  const handleCloseFontSize = () => {
    setAnchorEl(null);
  };

  const clearSelectedLanguage = () => {
    onChangeText(originalDraftMessage || '');
    setDraftLanguage?.('');
    setOriginalDraftMessage?.('');
    setTranslatedDraftMessage?.('');
    if (setTranslatedDraftObject)
      setTranslatedDraftObject(text, null);
  };

  const handleDraftLanguage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (setDraftLanguage)
      setDraftLanguage(event.target.value);
  };

  const handleSwitchDraftMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (setSwitchDraftMessage)
      setSwitchDraftMessage(event.target.checked);
  };

  const HeadingMenu = () => (
    <Menu
      anchorEl={anchorHeadingEl}
      id='heading-menu'
      open={openHeadingMenu}
      onClick={handleCloseHeading}
      onClose={handleCloseHeading}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: {
            width: '180px',
          },
        },
      }}
    >
      <MenuItem onClick={handleToggleHeadingOne}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.one} fontWeight={'bold'}>
            <FormattedMessage defaultMessage='Heading 1' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 1,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingTwo}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.two} fontWeight={'bold'}>
            <FormattedMessage defaultMessage='Heading 2' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 2,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingThree}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.three}>
            <FormattedMessage defaultMessage='Heading 3' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 3,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingFour}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.four}>
            <FormattedMessage defaultMessage='Heading 4' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 4,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingFive}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.five}>
            <FormattedMessage defaultMessage='Heading 5' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 5,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingSix}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.six}>
            <FormattedMessage defaultMessage='Heading 6' />
          </Typography>
          {initialEditor.isActive('heading', {
            level: 6,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
    </Menu>
  );

  const FontSizeMenu = () => (
    <Menu
      anchorEl={anchorEl}
      id='font-size-menu'
      open={openFontSizeMenu}
      onClick={handleCloseFontSize}
      onClose={handleCloseFontSize}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: {
            width: '140px',
          },
        },
      }}
    >
      <MenuItem onClick={handleToggleSmallFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.small + 'px'}>
            <FormattedMessage defaultMessage='Small' />
          </Typography>
          {initialEditor.isActive('textStyle', {
            fontSize: fontSizes.small,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleNormalFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <FormattedMessage defaultMessage='Normal' />
          {!initialEditor.isActive('textStyle') && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleMediumFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.medium + 'px'}>
            <FormattedMessage defaultMessage='Medium' />
          </Typography>
          {initialEditor.isActive('textStyle', {
            fontSize: fontSizes.medium,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleLargeFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.large + 'px'}>
            <FormattedMessage defaultMessage='Large' />
          </Typography>
          {initialEditor.isActive('textStyle', {
            fontSize: fontSizes.large,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #DADADA',
        borderRadius: () => (variant === 'message' ? '0px 0px 6px 6px' : '6px'),
        height: '100%',
        '&:focus-within': {
          border: () =>
            variant === 'message' ? '1px solid #DADADA' : '1px solid #181818',
        },
        backgroundColor: () =>
          initialEditor.isFocused ? 'white' : '#ffffff29',
        width: 'inherit',
        overflowY: 'hidden',
      }}
    >
      <div
        style={{
          minHeight: '180px',
          maxHeight: '95%',
          overflowY: 'auto',
          height: expand ? '95%' : '180px',
          marginLeft: '10px',
        }}
      >
        <EditorContent editor={initialEditor} />
      </div>
      {/* Editor Menu, had to be relocated here due to erratic focus behavior with rerendering especially messages when decoupled */}
      {attachedItems}
      {isEditable && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            padding: 1,
            borderTop: '1px solid #D6D6D6',
            alignItems: 'center',
          }}
        >
          <>
            <Tooltip title={toolTipMessages.formatting}>
              <ToggleButton
                value='toggleFormatting'
                sx={{
                  border: 0,
                }}
                color='teal'
                selected={formattingEnabled}
                onChange={() => {
                  setFormattingEnabled(!formattingEnabled);
                  initialEditor
                    .chain()
                    .selectAll()
                    .unsetAllMarks()
                    .focus()
                    .run();
                }}
              >
                <AbcIcon />
              </ToggleButton>
            </Tooltip>
            <Divider
              flexItem
              orientation='vertical'
              sx={{
                my: 1,
                mx: 1,
              }}
            />
          </>
          {formattingEnabled && (
            <>
              {variant === 'program' && (
                <>
                  <Tooltip title={toolTipMessages.heading}>
                    <ToggleButton
                      value='heading'
                      sx={{
                        border: 0,
                        mr: 0.5,
                      }}
                      color='teal'
                      selected={initialEditor.isActive('heading')}
                      onClick={handleHeadingButtonClick}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Schibsted Grotesk',
                          fontWeight: 500,
                          fontSize: fontSizes.medium + 'px',
                        }}
                      >
                        H+
                      </Typography>
                      <ArrowDropDownIcon />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title={toolTipMessages.fontSize}>
                    <ToggleButton
                      value='fontSize'
                      sx={{
                        border: 0,
                        mr: 0.5,
                      }}
                      color='teal'
                      selected={initialEditor.isActive('textStyle')}
                      onClick={handleFontSizeButtonClick}
                    >
                      <FormatSizeIcon />
                      <ArrowDropDownIcon />
                    </ToggleButton>
                  </Tooltip>
                  <HeadingMenu />
                  <FontSizeMenu />
                  <Divider
                    flexItem
                    orientation='vertical'
                    sx={{
                      my: 1,
                      mx: 1,
                    }}
                  />
                </>
              )}
              <>
                <Tooltip title={toolTipMessages.bold}>
                  <ToggleButton
                    value='bold'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={initialEditor.isActive('bold')}
                    onClick={handleToggleBold}
                  >
                    <FormatBoldIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={toolTipMessages.underline}>
                  <ToggleButton
                    value='underline'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={initialEditor.isActive('underline')}
                    onClick={handleToggleUnderline}
                  >
                    <FormatUnderlinedIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={toolTipMessages.italic}>
                  <ToggleButton
                    value='italic'
                    sx={{
                      border: 0,
                      mr: 0.5,
                    }}
                    color='teal'
                    selected={initialEditor.isActive('italic')}
                    onClick={handleToggleItalic}
                  >
                    <FormatItalicIcon />
                  </ToggleButton>
                </Tooltip>
              </>
              {(variant === 'program' || variant === 'message') && (
                <>
                  <Divider
                    flexItem
                    orientation='vertical'
                    sx={{
                      my: 1,
                      mx: 1,
                    }}
                  />
                  <Tooltip title={toolTipMessages.bulletList}>
                    <ToggleButton
                      value='bulletList'
                      sx={{
                        border: 0,
                        mr: 0.5,
                      }}
                      color='teal'
                      selected={initialEditor.isActive('bulletList')}
                      onClick={handleToggleBulletList}
                    >
                      <FormatListBulletedIcon />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title={toolTipMessages.numberedList}>
                    <ToggleButton
                      value='numberedList'
                      sx={{
                        border: 0,
                        mr: 0.5,
                      }}
                      color='teal'
                      selected={initialEditor.isActive('orderedList')}
                      onClick={handleToggleNumberedList}
                    >
                      <FormatListNumberedIcon />
                    </ToggleButton>
                  </Tooltip>
                </>
              )}
            </>
          )}
          {enableTranslate && (
            <>
              <Divider
                flexItem
                orientation='vertical'
                sx={{
                  my: 1,
                  mx: 1,
                }}
              />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
              }}
              >
                {!loadingDraftTranslation ?
                <>
                  <Typography sx={{ ...secondarySentence, mr: 1 }}>
                    <FormattedMessage defaultMessage='Translate:'/>
                  </Typography>
                  {!draftLanguage ?
                    <TextField
                      id='select-language'
                      select
                      variant='standard'
                      defaultValue=''
                      InputLabelProps={{
                        shrink: false,
                      }}
                      SelectProps = {{
                        disableUnderline: true,
                        displayEmpty: true,
                      }}
                      sx={{
                        width: '70px',
                        mr: 1,
                        '& .MuiSvgIcon-root': {
                          top: 0,
                        },
                      }}
                      value={draftLanguage}
                      onChange={handleDraftLanguage}
                      disabled={!text}
                      size='small'
                      margin='dense'
                    >
                      <MenuItem value='' sx={{ display: 'none' }}>
                        <Typography sx={{ ...secondarySentence }}>
                          <FormattedMessage defaultMessage='Select:'/>
                        </Typography>
                      </MenuItem>
                      {tenantLanguages.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<RadioButtonCheckedIcon color='primary'/>}
                              checked={tenantLanguages.some((lang : Language) => draftLanguage === lang.code)}
                            />
                            <ListItemText primary={item.name} />
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                    :
                  <>
                    <Typography
                      sx={{
                        ...secondarySentence,
                      }}
                    >
                      {tenantLanguages.find((item : Language) => draftLanguage === item.code)?.name}
                      <IconButton
                        aria-label='remove'
                        onClick={clearSelectedLanguage}
                        sx={{
                          fontSize: '14px',
                        }}
                      >
                        <CloseIcon fontSize="inherit"/>
                      </IconButton>
                    </Typography>
                    <Typography component='div'>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!switchDraftMessage}
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<RadioButtonCheckedIcon sx={{ color: '#00CECF' }} />}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                            onChange={handleSwitchDraftMessage}
                          />
                        }
                        label={
                          <Typography fontSize={14} color='#00CECF'>
                            <FormattedMessage defaultMessage='Original'/>
                          </Typography>
                        }
                      />
                    </Typography>
                  </>
                  }
                </> :
                <CircularProgress size={20} />}
              </Box>
            </>
          )}
          {(variant === 'message' && !loadingDraftTranslation) && messageButton}
        </Box>
      )}
    </Box>
  );
};

export default StandardTextEditor;
